import "./styles.scss";

import { useMemo } from "react";

import { ReactComponent as Logo } from "@assets/logo/M_WHITE_LOGO_FULL.svg";

import { Text, Button, Icon } from "@fragments";
import {
  IconSparkles,
  IconArrowRight,
  IconPercentage,
  IconClockHour4,
  IconCoin,
  IconFileText,
  IconExternalLink,
  IconMail,
  IconPhoto,
  IconLogin2,
  IconAlertTriangle,
  IconLinkOff,
  IconCircleCheck,
  IconCircleX,
} from "@tabler/icons-react";

import { DURATION_UNIT_LABEL } from "@pages/CommunityConfig/tabs/Mentoring/enums/durationUnits";

import { INVITE_STATE } from "../enums/inviteStates";

/**
 * Public mentoring invite (§6.9). A centered, shell-less landing: program banner,
 * name, description, terms and the gated accept/decline area — plus self-contained
 * status screens for the token (404/expired/used) and post-decline outcomes. No
 * app shell (it renders outside it, §10), so it carries its own brand + footer.
 */
export default function MentoringInviteView(props) {
  const { state } = props;

  if (state === INVITE_STATE.LOADING) {
    return (
      <InviteShell>
        <InviteSkeleton />
      </InviteShell>
    );
  }

  if (state === INVITE_STATE.VALID) {
    return (
      <InviteShell>
        <InviteCard {...props} />
      </InviteShell>
    );
  }

  return (
    <InviteShell>
      <InviteStatus state={state} />
    </InviteShell>
  );
}

// ── Page frame: brand mark · centered content · footer ─────────────────────────
function InviteShell({ children }) {
  return (
    <div className="mentoring-invite">
      <header className="mi-brand">
        {/* SVG logo (currentColor) recoloured per theme via --surface-2, mirroring
            src/Screen's header/sidebar logos. */}
        <Logo className="mi-brand-logo" />
      </header>

      <main className="mi-main">{children}</main>

      <footer className="mi-foot">
        <Text.span contrast={5}>Programa de Mentoria · Ginastee</Text.span>
      </footer>
    </div>
  );
}

// ── The live invite card (VALID) ───────────────────────────────────────────────
function InviteCard({
  program,
  invitedEmail,
  accountEmail,
  authResolved,
  isLoggedIn,
  emailMismatch,
  submitting,
  accept,
  decline,
  login,
  switchAccount,
}) {
  const paragraphs = useMemo(
    () =>
      (program?.description || "")
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter(Boolean),
    [program],
  );

  const splitPct = program?.split_percentage;
  const durationText = program?.duration_value
    ? `${program.duration_value} ${DURATION_UNIT_LABEL[program.duration_unit] ?? ""}`.trim()
    : "Indefinida";
  const splitWindowText = program?.split_duration_value
    ? `${program.split_duration_value} ${DURATION_UNIT_LABEL[program.split_duration_unit] ?? ""}`.trim()
    : null;

  return (
    <article className="mi-card">
      {/* Banner */}
      <div className="mi-banner" style={{ "--i": 0 }}>
        {program?.banner?.url ? (
          <img src={program.banner.url} alt={program?.name} loading="lazy" />
        ) : (
          <div className="mi-banner-fallback">
            <IconPhoto size={30} />
          </div>
        )}
      </div>

      <div className="mi-body">
        <span className="mi-eyebrow" style={{ "--i": 1 }}>
          <Text.span contrast={6} left={<IconSparkles size={14} />}>
            Convite para mentoria
          </Text.span>
        </span>

        <div className="mi-title" style={{ "--i": 2 }}>
          <Text.h2 contrast={9} weight={500}>
            {program?.name}
          </Text.h2>
        </div>

        {paragraphs.length > 0 && (
          <div className="mi-desc" style={{ "--i": 3 }}>
            {paragraphs.map((p, i) => (
              <Text.p key={i} contrast={6} className="mi-desc-p">
                {p}
              </Text.p>
            ))}
          </div>
        )}

        {/* Terms */}
        <div className="mi-terms" style={{ "--i": 4 }}>
          <TermRow
            icon={<IconPercentage size={14} />}
            label="Repasse de receita"
            value={splitPct != null ? `${splitPct}%` : "—"}
            emphasis
          />
          <TermRow
            icon={<IconClockHour4 size={14} />}
            label="Duração da mentoria"
            value={durationText}
          />
          {splitWindowText && (
            <TermRow
              icon={<IconCoin size={14} />}
              label="Janela de repasse"
              value={splitWindowText}
            />
          )}

          {program?.contract_url && (
            <a
              className="mi-contract"
              href={program.contract_url}
              target="_blank"
              rel="noreferrer"
            >
              <Text.span
                contrast={7}
                left={<IconFileText size={15} />}
                right={<IconExternalLink size={13} />}
              >
                Ver contrato do programa
              </Text.span>
            </a>
          )}
        </div>

        {/* Who it's for — hidden on a mismatch so we never reveal the recipient
            address to someone signed in as a different account. */}
        {!emailMismatch && (
          <div className="mi-invited" style={{ "--i": 5 }}>
            <span className="mi-invited-icon">
              <IconMail size={15} />
            </span>
            <Text.span contrast={5}>
              Convite enviado para <strong>{invitedEmail}</strong>
            </Text.span>
          </div>
        )}

        <div className="mi-divider" />

        {/* Gated actions */}
        <div className="mi-actions" style={{ "--i": 6 }}>
          {!authResolved ? (
            <div className="mi-checking">
              <span className="mi-spinner" aria-hidden="true" />
              <Text.span contrast={5}>Verificando sua conta…</Text.span>
            </div>
          ) : !isLoggedIn ? (
            <>
              <Button
                text="Entrar para responder"
                color="primary"
                size="large"
                startIcon={<IconLogin2 />}
                onClick={login}
                preventLoading
                fullWidth
              />
              <Text.span contrast={5} className="mi-hint">
                Entre na sua conta para aceitar ou recusar este convite.
              </Text.span>
            </>
          ) : emailMismatch ? (
            <div className="mi-mismatch">
              <div className="mi-mismatch-head">
                <span className="mi-mismatch-icon">
                  <IconAlertTriangle size={18} />
                </span>
                <div className="mi-mismatch-text">
                  <Text.span contrast={8} weight={500}>
                    Conta diferente do convite
                  </Text.span>
                  <Text.span contrast={5}>
                    Você está conectado como <strong>{accountEmail}</strong>, mas
                    este convite foi enviado para outro e-mail. Entre em contato com
                    o mentor e informe o e-mail para o qual o convite deve ser
                    enviado.
                  </Text.span>
                </div>
              </div>
              <Button
                text="Entrar com outra conta"
                color="overlay"
                variant="rounded"
                size="small"
                onClick={switchAccount}
                preventLoading
                fullWidth
              />
            </div>
          ) : (
            <div className="mi-cta">
              <Button
                text="Aceitar convite"
                color="primary"
                size="large"
                endIcon={<IconArrowRight size={18} />}
                onClick={accept}
                disabled={submitting}
                fullWidth
              />
              <Button
                text="Recusar"
                color="overlay"
                variant="rounded"
                size="large"
                onClick={decline}
                disabled={submitting}
                preventLoading
                fullWidth
              />
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function TermRow({ icon, label, value, emphasis }) {
  return (
    <div className="mi-term">
      <Text.span contrast={5} left={icon}>
        {label}
      </Text.span>
      <Text.span contrast={emphasis ? 9 : 8} weight={emphasis ? 500 : 300}>
        {value}
      </Text.span>
    </div>
  );
}

// ── Terminal / status screens (404 · expired · used · declined · error) ────────
const STATUS_COPY = {
  [INVITE_STATE.NOT_FOUND]: {
    Icon: IconLinkOff,
    title: "Convite não encontrado",
    subtitle:
      "Este link de convite não existe ou foi removido. Confira o endereço ou peça um novo convite ao dono do programa.",
    cta: { label: "Ir para a Ginastee", href: "/" },
  },
  [INVITE_STATE.EXPIRED]: {
    Icon: IconAlertTriangle,
    title: "Convite expirado",
    subtitle:
      "O prazo para responder a este convite acabou. Peça um novo convite ao dono do programa.",
    cta: { label: "Ir para a Ginastee", href: "/" },
  },
  [INVITE_STATE.USED]: {
    Icon: IconCircleCheck,
    title: "Convite já utilizado",
    subtitle:
      "Este convite já foi respondido. Se você aceitou, acesse suas mentorias para continuar.",
    cta: { label: "Ir para minhas mentorias", href: "/mentoring" },
  },
  [INVITE_STATE.DECLINED]: {
    Icon: IconCircleX,
    title: "Convite recusado",
    subtitle:
      "Você recusou este convite de mentoria. O dono do programa foi avisado. Você já pode fechar esta página.",
    cta: { label: "Ir para a Ginastee", href: "/" },
  },
  [INVITE_STATE.ERROR]: {
    Icon: IconAlertTriangle,
    title: "Algo deu errado",
    subtitle:
      "Não foi possível carregar este convite agora. Tente novamente em alguns instantes.",
    cta: { label: "Tentar novamente", reload: true },
  },
};

function InviteStatus({ state }) {
  const copy = STATUS_COPY[state] ?? STATUS_COPY[INVITE_STATE.ERROR];
  const { Icon: StatusIcon, title, subtitle, cta } = copy;

  function onCta() {
    if (cta?.reload) window.location.reload();
    else if (cta?.href) window.location.href = cta.href;
  }

  return (
    <article className="mi-card mi-card--status">
      <div className="mi-status">
        <Icon.Sphere size={76}>
          <StatusIcon />
        </Icon.Sphere>
        <Text.h3 contrast={8} weight={500}>
          {title}
        </Text.h3>
        <Text.span contrast={5} className="mi-status-sub">
          {subtitle}
        </Text.span>
        {cta && (
          <Button
            text={cta.label}
            color="inverse"
            variant="rounded"
            onClick={onCta}
            preventLoading
          />
        )}
      </div>
    </article>
  );
}

// ── Loading skeleton ───────────────────────────────────────────────────────────
function InviteSkeleton() {
  return (
    <article className="mi-card is-skeleton" aria-busy="true">
      <div className="mi-banner skel" />
      <div className="mi-body">
        <div className="skel skel-line" style={{ width: "40%", height: 20 }} />
        <div className="skel skel-line" style={{ width: "75%", height: 28 }} />
        <div className="skel skel-line" style={{ width: "100%", height: 60 }} />
        <div className="skel skel-line" style={{ width: "100%", height: 120 }} />
        <div className="skel skel-line" style={{ width: "100%", height: 48 }} />
      </div>
    </article>
  );
}
